import { FC, useCallback, useMemo } from 'react';
import {
  FormComponent,
  FormStructure,
  IFormData,
} from '../../../components/FormHelper/FormHelper.types';
import { StyleSheet, View } from 'react-native';
import { Field, FieldProps, Formik } from 'formik';
import { STYLE_VARS } from '../../../constants/style-vars';
import { TextInput } from '../../../components/TextInput/TextInput';
import { Button } from '../../../components/Button';
import DropDown from '../../../components/DropDown/DropDown';
import { COLORS, FONTS } from '../../../constants';

type ContactUsFormProps = {
  onSubmitHandler?: (data: IFormData) => void;
};

const ContactUsForm: FC<ContactUsFormProps> = ({ onSubmitHandler }) => {
  const initData = useMemo(() => {
    return {
      firstName: '',
      lastName: '',
      department: '',
      country: '',
      phone: '',
      message: '',
    };
  }, []);

  const contactUsStructure = useMemo(() => {
    const structure: FormStructure = {
      components: [
        {
          name: 'firstName',
          type: 'text',
          placeholder: 'FIRST NAME',
        },
        {
          name: 'lastName',
          type: 'text',
          placeholder: 'LAST NAME',
        },
        {
          name: 'department',
          type: 'dropdown',
          placeholder: 'DEPARTMENT',
          items: [
            { label: 'GENERAL ENQUIRIES', value: 'general' },
            { label: 'MEMBERSHIP ENQUIRIES', value: 'membership' },
            {
              label: 'SPORTS & RECREATION ENQUIRIES',
              value: 'sportAndRecreation',
            },
            { label: 'PRIVATE FUNCTION ENQUIRIES', value: 'privateFunction' },
            { label: 'FOOD & BEVERAGE ENQUIRIES', value: 'FoodAndBeverage' },
          ],
        },
        {
          name: 'country',
          type: 'dropdown',
          placeholder: 'COUNTRY',
          items: [],
        },
        {
          name: 'phone',
          type: 'text',
          placeholder: 'PHONE',
        },
      ],
    };
    return structure;
  }, []);

  const renderForm = useCallback((component: FormComponent) => {
    switch (component.type) {
      case 'text':
        return (
          <Field name={component.name}>
            {({ field }: FieldProps) => (
              <View style={{ marginBottom: 10 }} key={component.name}>
                <TextInput
                  placeholder={component.placeholder || 'Input ...'}
                  onChangeText={field.onChange(field.name)}
                  value={field.value}
                  id={field.name}
                  placeholderTextColor={STYLE_VARS.fccDarkerGrey}
                  style={styles.input}
                />
              </View>
            )}
          </Field>
        );
      case 'dropdown':
        return (
          <Field name={component.name}>
            {({ field }: FieldProps) => (
              <View style={{ marginBottom: 10 }} key={component.name}>
                <DropDown
                  items={component.items || []}
                  title={component.label || ''}
                  placeholder={component.placeholder || 'SELECT'}
                  dropdownStyle={{
                    paddingHorizontal: 10,
                    borderColor: STYLE_VARS.fccDarkGrey,
                    borderWidth: 1,
                  }}
                  textStyle={{
                    color: STYLE_VARS.fccDarkerGrey,
                    fontFamily: FONTS.AvenirMedium,
                  }}
                  customUpButton={require('../../../assets/up-arrow.png')}
                  customDownButton={require('../../../assets/down-arrow.png')}
                />
              </View>
            )}
          </Field>
        );
      default:
        return <></>;
    }
  }, []);

  return (
    <View>
      <Formik
        initialValues={initData}
        onSubmit={(value) => {
          if (onSubmitHandler) onSubmitHandler(value);
        }}
      >
        {({ handleSubmit, handleChange, values }) => (
          <View style={{ marginVertical: 20 }}>
            {contactUsStructure.components.map((component, index) => (
              <View key={index}>{renderForm(component)}</View>
            ))}
            <TextInput
              placeholder={'MESSAGE'}
              onChangeText={handleChange('message')}
              value={values.message}
              textAlignVertical="top"
              multiline
              placeholderTextColor={STYLE_VARS.fccDarkerGrey}
              style={styles.textarea}
            />
            <View style={{ alignSelf: 'center', marginBottom: 40 }}>
              <Button
                text="SUBMIT"
                style={STYLE_VARS.blueButton}
                onPress={() => {
                  handleSubmit();
                }}
              />
            </View>
          </View>
        )}
      </Formik>
    </View>
  );
};

export default ContactUsForm;

const styles = StyleSheet.create({
  input: {
    paddingHorizontal: 10,
    paddingVertical: 7,
    borderWidth: 1,
    borderColor: STYLE_VARS.fccDarkGrey,
    fontFamily: FONTS.AvenirMedium,
    fontSize: 12,
    fontWeight: '500',
    color: STYLE_VARS.fccDarkerGrey,
  },
  textarea: {
    height: 100,
    paddingHorizontal: 10,
    alignItems: 'flex-start',
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: COLORS.DARK_GREY,
    color: COLORS.DARKER_GREY,
    fontSize: 12,
    fontWeight: '500',
    fontFamily: FONTS.AvenirMedium,
    marginBottom: 30,
  },
});
